import { h, readonly, Ref, VNode } from 'vue'

import {
  IncidentAttributeId,
  IncidentsQueuePatchDTO,
} from '@collector/sportsapi-client-legacy'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'

import {
  AttributeConfig,
  DetailButtonsConfiguration,
  DetailConfiguration,
  DetailSelectConfiguration,
  getAttributeId,
  isDetailButtonsConfiguration,
  isDetailSelectConfiguration,
} from '../../../Sports/composables/useSportOptions'
import ButtonsGroup from '../ButtonsGroup.vue'
import GoalkeeperSelectGroup from '../GoalkeeperSelectGroup.vue'
import SelectGroup from '../SelectGroup.vue'
import { useIncidentDetailsForm } from './useIncidentDetailsForm'

type UseIncidentGroupsAttributesSelection<T extends IncidentAttributeId> = {
  getGroupComponent: (group: DetailConfiguration<T>) => VNode | null
  updateIncident: (dto?: Partial<IncidentsQueuePatchDTO>) => void
  selectedAttributes: Readonly<Ref<readonly IncidentAttributeId[]>>
  addAttribute: (attributeId: IncidentAttributeId) => void
  removeAttribute: (attributeId: IncidentAttributeId) => void
  toggleGroupAttribute: (
    group: DetailButtonsConfiguration<T>,
    attributeId: IncidentAttributeId,
  ) => void
  addGroupAttribute: (
    group: DetailButtonsConfiguration<T>,
    attributeId: IncidentAttributeId,
  ) => void
}

export function useIncidentGroupComponents<T extends IncidentAttributeId>(
  linkedIncident: ProbableLinkedIncident,
  groups: Map<string, DetailConfiguration<T>>,
): UseIncidentGroupsAttributesSelection<T> {
  const {
    toggleAttribute,
    addAttribute,
    removeAttribute,
    selectedAttributes,
    updateIncident,
    isSomeAttributeSelected,
  } = useIncidentDetailsForm(linkedIncident.incident)

  function addGroupAttribute(
    group: DetailButtonsConfiguration<T>,
    attributeId: IncidentAttributeId,
  ): void {
    if (group.multiple) {
      addAttribute(attributeId)
      return
    }

    group.attributes
      .map(getAttributeId)
      .filter((id) => id !== attributeId)
      .forEach(removeAttribute)

    addAttribute(attributeId)
  }

  function toggleGroupAttribute(
    group: DetailButtonsConfiguration<T>,
    attributeId: IncidentAttributeId,
  ): void {
    if (group.multiple) {
      toggleAttribute(attributeId)
    } else {
      if (selectedAttributes.value.includes(attributeId)) {
        removeAttribute(attributeId)
        return
      }

      group.attributes.forEach((attr) => removeAttribute(getAttributeId(attr)))

      addAttribute(attributeId)
    }
  }

  function handleAttributeSelected(attributeId: IncidentAttributeId): void {
    const group = Array.from(groups.values()).find(
      (g) =>
        isDetailButtonsConfiguration(g) &&
        g.attributes.some((attr) => getAttributeId(attr) === attributeId),
    )

    if (!group || !isDetailButtonsConfiguration(group)) return

    if (group.onSelect) {
      group.onSelect({
        addAttribute,
        attributes: group.attributes,
        removeAttribute,
        selectedAttributes,
        toggleAttribute,
        attributeId,
      })
      return
    }

    toggleGroupAttribute(group, attributeId)
  }

  function getButtonsGroupComponent(
    group: DetailButtonsConfiguration<T>,
  ): VNode | null {
    return h(ButtonsGroup, {
      name: group.name,
      important: group.important,
      isFulfilled: group.fulfilledWhen
        ? group.fulfilledWhen(
            selectedAttributes.value as T[],
            linkedIncident.incident,
          )
        : isSomeAttributeSelected(group.attributes.map(getAttributeId)),
      attributesOrAttributeConfigs: group.attributes as (
        IncidentAttributeId | AttributeConfig<IncidentAttributeId>
      )[],
      cols: group.cols || 2,
      linkedIncident,
      selectedAttributes: selectedAttributes.value,
      onAttributeSelected: handleAttributeSelected,
    })
  }

  function getSelectGroupComponent(
    group: DetailSelectConfiguration<T>,
  ): VNode | null {
    if (group.name.includes('Goalkeeper')) {
      return h(GoalkeeperSelectGroup, {
        name: group.name,
        linkedIncident,
      })
    }

    return h(SelectGroup, {
      name: group.name,
      trackBy: 'name',
      options: group.optionsFactory(linkedIncident),
    })
  }

  function getGroupComponent(group: DetailConfiguration<T>): VNode | null {
    if (isDetailButtonsConfiguration(group)) {
      return getButtonsGroupComponent(group)
    } else if (isDetailSelectConfiguration(group)) {
      return getSelectGroupComponent(group)
    }
    return null
  }

  return {
    selectedAttributes: readonly(selectedAttributes),
    getGroupComponent,
    updateIncident,
    addAttribute,
    removeAttribute,
    toggleGroupAttribute,
    addGroupAttribute,
  }
}
